import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { fechaCorta } from "@/components/contratos/constants";

const cuando = (x) => x.taken_at || x.created_at || "";
const diaDe = (x) => new Date(cuando(x)).toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" }); // AAAA-MM-DD
const hora = (x) => new Date(cuando(x)).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" });
const tituloDia = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

// Galería agrupada por día (más reciente primero). Usa la hora de captura si existe, si no la de subida.
export const GaleriaPortal = ({ fotos, fotoUrl }) => {
  const [i, setI] = useState(-1);
  const { lista, grupos } = useMemo(() => {
    const orden = [...fotos].sort((a, b) => cuando(b).localeCompare(cuando(a)));
    const g = [];
    orden.forEach((x, n) => {
      const d = diaDe(x);
      if (!g.length || g[g.length - 1].dia !== d) g.push({ dia: d, items: [] });
      g[g.length - 1].items.push({ x, n });
    });
    return { lista: orden, grupos: g };
  }, [fotos]);
  const mover = (d) => setI((x) => (x + d + lista.length) % lista.length);
  useEffect(() => {
    if (i < 0) return undefined;
    const tecla = (e) => { if (e.key === "ArrowRight") mover(1); if (e.key === "ArrowLeft") mover(-1); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  });
  const f = lista[i];
  return (
    <>
      <div className="space-y-5">
        {grupos.map((g) => (
          <section key={g.dia} data-testid={`portal-dia-${g.dia}`}>
            <h4 className="text-xs font-semibold text-slate-500 capitalize mb-2">{tituloDia(g.dia)}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {g.items.map(({ x, n }) => (
                <button key={x.id} onClick={() => setI(n)} className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-200" data-testid={`portal-foto-${x.id}`}>
                  <img src={fotoUrl(x.id)} alt={x.titulo || "Avance de la obra"} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5 text-left text-[11px] text-white">{hora(x)}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      <Dialog open={i >= 0} onOpenChange={(v) => { if (!v) setI(-1); }}>
        <DialogContent className="max-w-5xl w-[calc(100vw-1rem)] p-0 bg-black border-0 overflow-hidden" data-testid="portal-foto-visor">
          <DialogTitle className="sr-only">Foto de la obra</DialogTitle>
          <DialogDescription className="sr-only">Foto {i + 1} de {lista.length}</DialogDescription>
          {f && <img src={fotoUrl(f.id)} alt={f.titulo || "Avance de la obra"} className="w-full max-h-[80vh] object-contain" />}
          {lista.length > 1 && <>
            <button onClick={() => mover(-1)} aria-label="Anterior" className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white" data-testid="portal-foto-anterior"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => mover(1)} aria-label="Siguiente" className="absolute right-12 sm:right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white" data-testid="portal-foto-siguiente"><ChevronRight className="w-5 h-5" /></button>
          </>}
          {f && <p className="px-4 py-2 text-xs text-white/80">{f.titulo ? `${f.titulo} · ` : ""}{fechaCorta(cuando(f))} {hora(f)} · {i + 1}/{lista.length}</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};
